import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, TrendingUp, TrendingDown, History, Edit2, Trash2, Check, X } from "lucide-react";
import api from "../api";

const fetchStock = () => api.get("/warehouse/stock").then(r => r.data);
const fetchProducts = () => api.get("/warehouse/products").then(r => r.data);
const fetchMovements = (product_id) =>
  api.get("/warehouse/movements", { params: { product_id, limit: 100 } }).then(r => r.data);

const IN_TYPES = [
  "New batch from publisher",
  "Amazon FBA return",
  "Student return",
  "Retailer return",
  "Other IN",
];

const OUT_TYPES = [
  "Ship to FBA",
  "Direct to student (FBM)",
  "Retailer / bulk order",
  "Damaged / write-off",
  "Other OUT",
];

export default function Warehouse() {
  const qc = useQueryClient();
  const { data: stock } = useQuery({ queryKey: ["warehouse-stock"], queryFn: fetchStock });
  const { data: products } = useQuery({ queryKey: ["warehouse-products"], queryFn: fetchProducts });
  const [showAdd, setShowAdd] = useState(false);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [form, setForm] = useState({
    product_id: "", direction: "IN", movement_type: IN_TYPES[0],
    quantity: "", reference: "", notes: ""
  });

  const { data: movements } = useQuery({
    queryKey: ["movements", historyProduct],
    queryFn: () => fetchMovements(historyProduct),
    enabled: !!historyProduct
  });

  const addMovement = useMutation({
    mutationFn: (data) => api.post("/warehouse/movement", data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries(["warehouse-stock"]);
      qc.invalidateQueries(["movements", historyProduct]);
      setShowAdd(false);
      setForm({ product_id: "", direction: "IN", movement_type: IN_TYPES[0], quantity: "", reference: "", notes: "" });
    }
  });

  const editMovement = useMutation({
    mutationFn: ({ id, data }) => api.put(`/warehouse/movement/${id}`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries(["warehouse-stock"]);
      qc.invalidateQueries(["movements", historyProduct]);
      setEditingId(null);
    }
  });

  const deleteMovement = useMutation({
    mutationFn: (id) => api.delete(`/warehouse/movement/${id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries(["warehouse-stock"]);
      qc.invalidateQueries(["movements", historyProduct]);
    }
  });

  const startEdit = (m) => {
    setEditingId(m.id);
    setEditForm({
      direction: m.direction,
      movement_type: m.type,
      quantity: m.quantity,
      reference: m.reference || "",
      notes: m.notes || ""
    });
  };

  const totalStock = stock?.reduce((s, r) => s + r.current_quantity, 0) || 0;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Warehouse Stock</div>
          <div className="page-subtitle">Track your warehouse inventory by product</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(!showAdd)}>
          <Plus size={14} /> Add Movement
        </button>
      </div>

      {/* Add movement form */}
      {showAdd && (
        <div className="chart-card" style={{ marginBottom: "1.5rem" }}>
          <div className="chart-title">Record Stock Movement</div>
          <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
            {["IN", "OUT"].map(d => (
              <button key={d} onClick={() => setForm(f => ({
                ...f, direction: d,
                movement_type: d === "IN" ? IN_TYPES[0] : OUT_TYPES[0]
              }))}
                style={{
                  padding: "8px 24px", borderRadius: "8px", fontWeight: 600,
                  cursor: "pointer", border: "none",
                  background: form.direction === d ? (d === "IN" ? "#38a169" : "#e53e3e") : "#f0f0f0",
                  color: form.direction === d ? "white" : "#666"
                }}>
                {d === "IN" ? "▲ Stock IN" : "▼ Stock OUT"}
              </button>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px" }}>
            <div style={{ gridColumn: "span 2" }}>
              <label style={{ fontSize: "0.75rem", color: "#888", display: "block", marginBottom: "4px" }}>Product</label>
              <select value={form.product_id}
                onChange={e => setForm(f => ({ ...f, product_id: e.target.value }))}
                style={{ width: "100%", padding: "8px 10px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "0.875rem" }}>
                <option value="">-- Select Product --</option>
                {products?.map(p => <option key={p.id} value={p.id}>{p.product_name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: "0.75rem", color: "#888", display: "block", marginBottom: "4px" }}>Reason</label>
              <select value={form.movement_type}
                onChange={e => setForm(f => ({ ...f, movement_type: e.target.value }))}
                style={{ width: "100%", padding: "8px 10px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "0.875rem" }}>
                {(form.direction === "IN" ? IN_TYPES : OUT_TYPES).map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: "0.75rem", color: "#888", display: "block", marginBottom: "4px" }}>Quantity</label>
              <input type="number" min="1" value={form.quantity}
                onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
                style={{ width: "100%", padding: "8px 10px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "0.875rem" }} />
            </div>
            <div>
              <label style={{ fontSize: "0.75rem", color: "#888", display: "block", marginBottom: "4px" }}>Reference (optional)</label>
              <input value={form.reference} placeholder="e.g. Invoice #123"
                onChange={e => setForm(f => ({ ...f, reference: e.target.value }))}
                style={{ width: "100%", padding: "8px 10px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "0.875rem" }} />
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <label style={{ fontSize: "0.75rem", color: "#888", display: "block", marginBottom: "4px" }}>Notes (optional)</label>
              <input value={form.notes} placeholder="Any additional notes"
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                style={{ width: "100%", padding: "8px 10px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "0.875rem" }} />
            </div>
          </div>
          <div style={{ marginTop: "12px", display: "flex", gap: "8px" }}>
            <button className="btn btn-primary btn-sm"
              onClick={() => addMovement.mutate({
                ...form,
                product_id: parseInt(form.product_id),
                quantity: parseInt(form.quantity)
              })}>
              <Plus size={14} /> Save Movement
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
          {addMovement.isError && (
            <div className="alert" style={{ marginTop: "10px" }}>Failed to save. Please check all fields.</div>
          )}
        </div>
      )}

      {/* Stock table */}
      <div className="table-card" style={{ marginBottom: "1.5rem" }}>
        <div className="table-header">
          <span style={{ fontWeight: 600 }}>Current Warehouse Stock</span>
          <span style={{ fontSize: "0.8rem", color: "#888" }}>Total: {totalStock.toLocaleString()} units</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Qty in Warehouse</th>
                <th>Last Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {!stock?.length ? (
                <tr><td colSpan={4} style={{ textAlign: "center", padding: "2rem", color: "#aaa" }}>
                  No warehouse stock yet. Add your first movement above.
                </td></tr>
              ) : stock.map(row => (
                <tr key={row.product_id}>
                  <td style={{ fontWeight: 600 }}>📦 {row.product_name}</td>
                  <td style={{ fontWeight: 700, fontSize: "1.1rem",
                    color: row.current_quantity < 20 ? "#e53e3e"
                      : row.current_quantity < 50 ? "#dd6b20" : "#38a169" }}>
                    {row.current_quantity.toLocaleString()}
                  </td>
                  <td style={{ color: "#888", fontSize: "0.8rem" }}>
                    {new Date(row.updated_at).toLocaleDateString("en-IN")}
                  </td>
                  <td>
                    <button className="btn btn-outline btn-sm"
                      onClick={() => setHistoryProduct(
                        historyProduct === row.product_id ? null : row.product_id
                      )}>
                      <History size={13} /> History
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Movement history with edit/delete */}
      {historyProduct && movements && (
        <div className="table-card">
          <div className="table-header">
            <span style={{ fontWeight: 600 }}>
              Movement History — {stock?.find(s => s.product_id === historyProduct)?.product_name}
            </span>
            <button onClick={() => setHistoryProduct(null)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}>✕</button>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Date</th><th>Direction</th><th>Type</th><th>Qty</th><th>Reference</th><th>Notes</th><th>By</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {!movements.length ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: "1rem", color: "#aaa" }}>No movements yet</td></tr>
                ) : movements.map(m => (
                  <tr key={m.id} style={{ background: editingId === m.id ? "#f0f4ff" : undefined }}>
                    <td style={{ fontSize: "0.8rem" }}>{new Date(m.created_at).toLocaleDateString("en-IN")}</td>
                    <td>
                      {editingId === m.id ? (
                        <select value={editForm.direction}
                          onChange={e => setEditForm(f => ({ ...f, direction: e.target.value }))}
                          style={{ padding: "4px", borderRadius: "4px", border: "1px solid #e0e0e0", fontSize: "0.8rem" }}>
                          <option value="IN">IN</option>
                          <option value="OUT">OUT</option>
                        </select>
                      ) : (
                        <span style={{ display: "flex", alignItems: "center", gap: "4px",
                          color: m.direction === "IN" ? "#38a169" : "#e53e3e", fontWeight: 600 }}>
                          {m.direction === "IN" ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                          {m.direction}
                        </span>
                      )}
                    </td>
                    <td style={{ fontSize: "0.85rem" }}>
                      {editingId === m.id ? (
                        <select value={editForm.movement_type}
                          onChange={e => setEditForm(f => ({ ...f, movement_type: e.target.value }))}
                          style={{ padding: "4px", borderRadius: "4px", border: "1px solid #e0e0e0", fontSize: "0.8rem" }}>
                          {[...IN_TYPES, ...OUT_TYPES].map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      ) : m.type}
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      {editingId === m.id ? (
                        <input type="number" value={editForm.quantity}
                          onChange={e => setEditForm(f => ({ ...f, quantity: e.target.value }))}
                          style={{ width: "70px", padding: "4px", borderRadius: "4px", border: "1px solid #e0e0e0", fontSize: "0.8rem" }} />
                      ) : m.quantity}
                    </td>
                    <td style={{ fontSize: "0.8rem", color: "#888" }}>
                      {editingId === m.id ? (
                        <input value={editForm.reference}
                          onChange={e => setEditForm(f => ({ ...f, reference: e.target.value }))}
                          style={{ width: "100px", padding: "4px", borderRadius: "4px", border: "1px solid #e0e0e0", fontSize: "0.8rem" }} />
                      ) : (m.reference || "—")}
                    </td>
                    <td style={{ fontSize: "0.8rem", color: "#888" }}>
                      {editingId === m.id ? (
                        <input value={editForm.notes}
                          onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                          style={{ width: "100px", padding: "4px", borderRadius: "4px", border: "1px solid #e0e0e0", fontSize: "0.8rem" }} />
                      ) : (m.notes || "—")}
                    </td>
                    <td style={{ fontSize: "0.8rem" }}>{m.created_by}</td>
                    <td>
                      <div style={{ display: "flex", gap: "4px" }}>
                        {editingId === m.id ? (
                          <>
                            <button onClick={() => editMovement.mutate({ id: m.id, data: { ...editForm, quantity: parseInt(editForm.quantity) } })}
                              style={{ background: "#38a169", border: "none", borderRadius: "4px", padding: "4px 8px", cursor: "pointer", color: "white" }}>
                              <Check size={13} />
                            </button>
                            <button onClick={() => setEditingId(null)}
                              style={{ background: "#e53e3e", border: "none", borderRadius: "4px", padding: "4px 8px", cursor: "pointer", color: "white" }}>
                              <X size={13} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => startEdit(m)}
                              style={{ background: "none", border: "1px solid #e0e0e0", borderRadius: "4px", padding: "4px 8px", cursor: "pointer", color: "#4361ee" }}>
                              <Edit2 size={13} />
                            </button>
                            <button onClick={() => { if(window.confirm('Delete this movement? Stock will be reversed.')) deleteMovement.mutate(m.id); }}
                              style={{ background: "none", border: "1px solid #e0e0e0", borderRadius: "4px", padding: "4px 8px", cursor: "pointer", color: "#e53e3e" }}>
                              <Trash2 size={13} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
